import { FC, useState } from "react";
import {
  Box,
  Button,
  Container,
  Grid,
  IconButton,
  TextField,
} from "@mui/material";

import { isEmpty } from "lodash";
import { makeSxStyles } from "ui";
import { Visibility, VisibilityOff } from "@mui/icons-material";
import NavLink from "../../components/nav-link";
import { AdRoutes } from "../../constants/ad-routes";
import { applyAuthResponse, useAdminAuthControllerLogin } from "service";
import { useFormik } from "formik";
import { AdminLoginReqDto } from "service/api/estabrookSchemas";
import { toast } from "react-toastify";
import { setPendingPostAuthRedirect } from "../../features/settings/utils/pending-post-auth-redirect";

const LoginPage: FC = () => {
  const { mutate, isLoading } = useAdminAuthControllerLogin({
    onSuccess: (data) => {
      if (data?.data?.accessToken) {
        applyAuthResponse(data.data);
        setPendingPostAuthRedirect(AdRoutes.customer);
        toast.success("Login Successful");
      }
    },
  });
  const [showPassword, setShowPassword] = useState<boolean>(false);
  const styles = useStyles();

  const { values, handleBlur, handleChange, errors, submitForm } =
    useFormik<AdminLoginReqDto>({
      initialValues: {
        email: "",
        password: "",
      },
      onSubmit: (values) =>
        mutate({
          body: {
            ...values,
            email: values.email.toLowerCase(),
          },
        }),
    });

  return (
    <Container maxWidth="xs">
      <Box marginBottom="60px" display="flex" justifyContent="center">
        <img src="/snowsync-logo.png" alt="logo" style={{ width: "150px" }} />
      </Box>
      <Box
        component="form"
        onSubmit={(e) => {
          e.preventDefault();
          submitForm();
        }}
      >
        <Grid container rowSpacing={4} direction="column">
          <Grid item>
            <TextField
              id="email"
              value={values.email}
              onBlur={handleBlur}
              onChange={handleChange}
              placeholder="Email"
              type="email"
              fullWidth
              error={!isEmpty(errors.email)}
            />
          </Grid>
          <Grid item xs>
            <TextField
              id="password"
              value={values.password}
              onBlur={handleBlur}
              onChange={handleChange}
              placeholder="Password"
              type={showPassword ? "text" : "password"}
              error={!isEmpty(errors.email)}
              InputProps={{
                endAdornment: (
                  <IconButton
                    sx={styles.iconButton}
                    onClick={() => setShowPassword((prev) => !prev)}
                  >
                    {showPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                ),
              }}
              fullWidth
              helperText={
                !isEmpty(errors.email) && "Check your email and password"
              }
            />
          </Grid>
          <Grid item xs sx={styles.linkContainer}>
            <NavLink
              textAlign="center"
              sx={styles.link}
              linkTo={AdRoutes.forgotPassword}
            >
              Forgot password?
            </NavLink>
          </Grid>
          <Grid item xs>
            <Button
              type="submit"
              onSubmit={(e) => {
                e.preventDefault();
                submitForm();
              }}
              variant="contained"
              disabled={isLoading}
              fullWidth
              data-testid="login-button"
            >
              Login
            </Button>
          </Grid>
        </Grid>
      </Box>
    </Container>
  );
};

const useStyles = makeSxStyles(({ palette: { neutral } }) => ({
  linkContainer: {
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
  },
  link: {
    textDecoration: "none",
    margin: "0px auto",
  },
  iconButton: { color: neutral },
}));

export default LoginPage;
